import type { SxProps, Theme } from '@mui/material/styles';
import React from 'react';
import { FormProvider } from '../../form/FormContext';
import useForm from '../../form/useForm';
import { noop } from '../../lib';
import SaveShortcutScope from '../SaveShortcutScope';
import FormBase from './FormBase';

interface GenericFormProps {
  sx?: SxProps<Theme>;
  children?: React.ReactNode;
  onSubmit?: (values: Record<string, unknown>) => unknown;
  onSuccess?: (response: unknown) => void;
  onError?: (error: unknown, info: { isInvalid: boolean }) => void;
  onInvalid?: () => void;
  schema?: unknown;
  defaultValues?: Record<string, unknown>;
  showServerErrors?: boolean;
  mapServerErrorField?: (field: string) => string;
  onNonFieldError?: (messages: string[]) => void;
  saveShortcut?: boolean;
  onShortcutSave?: () => unknown;
}

export type FormSubmitResult =
  | { ok: true; response: unknown }
  | {
      ok: false;
      error?: unknown;
      reason: 'invalid' | 'server';
    };

const Form = React.forwardRef<HTMLFormElement, GenericFormProps>(
  (props, ref) => {
    const {
      sx,
      children,
      onSubmit = noop as GenericFormProps['onSubmit'],
      onSuccess = noop as GenericFormProps['onSuccess'],
      onError = noop as GenericFormProps['onError'],
      onInvalid = noop,
      saveShortcut = false,
      onShortcutSave,
      ...restProps
    } = props;
    const formRef = React.useRef<HTMLFormElement>(null);

    const { handleSubmit, ...form } = useForm(
      restProps as Record<string, unknown>,
    );
    React.useImperativeHandle(ref, () => formRef.current as HTMLFormElement);

    const submit = React.useCallback(
      () => new Promise<FormSubmitResult>((resolve) => {
        const submitHandler = handleSubmit({
          onSubmit,
          onSuccess: (response) => {
            onSuccess?.(response);
            resolve({ ok: true, response });
          },
          onError: (error, info) => {
            onError?.(error, info);
            resolve({
              ok: false,
              error,
              reason: info.isInvalid ? 'invalid' : 'server',
            });
          },
          onInvalid: () => {
            onInvalid();
            resolve({ ok: false, reason: 'invalid' });
          },
        });

        void submitHandler().catch((error: unknown) => {
          resolve({ ok: false, error, reason: 'server' });
        });
      }),
      [handleSubmit, onError, onInvalid, onSubmit, onSuccess],
    );

    const formElement = (
      <FormBase
        ref={formRef}
        sx={sx}
        onSubmit={() => {
          void submit();
        }}
      >
        {children}
      </FormBase>
    );

    const handleShortcutSave = () => {
      if (onShortcutSave) {
        return onShortcutSave();
      }

      formRef.current?.requestSubmit();
    };

    return (
      <FormProvider form={{ handleSubmit, submit, ...form }}>
        {saveShortcut ? (
          <SaveShortcutScope
            disabled={form.formState.isSubmitting}
            onSave={handleShortcutSave}
          >
            {formElement}
          </SaveShortcutScope>
        ) : formElement}
      </FormProvider>
    );
  },
);

Form.displayName = 'FormGeneric';
export default Form;
